/* eslint-disable no-param-reassign */
import Point from './point';

/**
 * @param {number[]} a
 * @return {number}
 */
function norm(a) {
    return (a[0] * a[0]) + (a[1] * a[1]);
}

/**
 * @param {number[]} a
 */
function normalize(a) {
    const mag = Math.sqrt(norm(a));
    if (a[0]) a[0] /= mag;
    if (a[1]) a[1] /= mag;
}

/**
 * @param {number} degParam
 * @return {number}
 */
function rad(degParam) {
    return ((degParam % 360) * Math.PI) / 180;
}

/**
 *
 * @param {number} radParam
 * @return {number}
 */
function deg(radParam) {
    return ((radParam * 180) / Math.PI) % 360;
}

/**
 * Creates a transform matrix to apply various transformation to points
 * in a two dimensional space.
 *
 * This class was extracted from the *Raphaël 2.1.0 - JavaScript Vector
 * Library
 *
 * @license
 * ### The MIT License
 *
 * Permission is hereby granted, free of charge, to any person obtaining a
 * copy of this software and associated documentation files (the “Software”),
 * to deal in the Software without restriction, including without limitation
 * the rights to use, copy, modify, merge, publish, distribute, sublicense,
 * and/or sell copies of the Software, and to permit persons to whom the
 * Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included
 * in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED “AS IS”, WITHOUT WARRANTY OF ANY KIND, EXPRESS
 * OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR
 * OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE,
 * ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR
 * OTHER DEALINGS IN THE SOFTWARE.
 *
 * @copyright 2008-2012 Dmitry Baranovskiy (http://raphaeljs.com)
 * @copyright 2008-2012 Sencha Labs (http://sencha.com)
 *
 * @class Matrix
 */
export default class Matrix {
    constructor(a, b, c, d, e, f) {
        if (a != null) {
            this.a = +a;
            this.b = +b;
            this.c = +c;
            this.d = +d;
            this.e = +e;
            this.f = +f;
        } else {
            this.a = 1;
            this.b = 0;
            this.c = 0;
            this.d = 1;
            this.e = 0;
            this.f = 0;
        }
    }

    /**
     * Adds given matrix to existing one. The given matrix can be an instance
     * of Matrix or the 6 number parameters which represent the matrix
     * values.
     *
     * @param {Matrix/number} a
     * @param {number} b
     * @param {number} c
     * @param {number} d
     * @param {number} e
     * @param {number} f
     */
    add(a, b, c, d, e, f) {
        const out = [[], [], []];
        const m = [[this.a, this.c, this.e], [this.b, this.d, this.f], [0, 0, 1]];
        let matrix = [[a, c, e], [b, d, f], [0, 0, 1]];

        if (a && a instanceof Matrix) {
            matrix = [[a.a, a.c, a.e], [a.b, a.d, a.f], [0, 0, 1]];
        }

        for (let x = 0; x < 3; x += 1) {
            for (let y = 0; y < 3; y += 1) {
                let res = 0;
                for (let z = 0; z < 3; z += 1) {
                    res += m[x][z] * matrix[z][y];
                }
                out[x][y] = res;
            }
        }

        const [
            [na, nc, ne],
            [nb, nd, nf],
        ] = out;

        this.a = na;
        this.b = nb;
        this.c = nc;
        this.d = nd;
        this.e = ne;
        this.f = nf;
    }

    /**
     * Inverted the current matrix
     *
     * @return {Matrix}
     * @chainable
     */
    invert() {
        const me = this.clone();
        const x = (me.a * me.d) - (me.b * me.c);

        this.a = me.d / x;
        this.b = -me.b / x;
        this.c = -me.c / x;
        this.d = me.a / x;
        this.e = ((me.c * me.f) - (me.d * me.e)) / x;
        this.f = ((me.b * me.e) - (me.a * me.f)) / x;

        return this;
    }

    /**
     * Returns copy of the matrix
     *
     * @return {Matrix} New matrix instance
     */
    clone() {
        return new Matrix(this.a, this.b, this.c, this.d, this.e, this.f);
    }

    /**
     * Add translation to the matrix. Translation is used to move a point.
     * The given values are added to the existing matrix.
     *
     * @param {number} x Offset on the x-axis
     * @param {number} y Offset on the y-axis
     * @return {Matrix}
     * @chainable
     */
    translate(x, y) {
        this.add(1, 0, 0, 1, x, y);
        return this;
    }

    /**
     * Scales the matrix to the given values. The values are added to the
     * existing matrix.
     *
     * @param {number} x Scale factor on the x-axis
     * @param {number} [y] x Scale factor on the y-axis
     * @param {number} [cx] Center point on the x-Axis
     * @param {number} [cy] Center point on the x-Axis
     * @return {Matrix}
     * @chainable
     */
    scale(x, y, cx, cy) {
        if (y == null) y = x;
        if (cx || cy) this.add(1, 0, 0, 1, cx, cy);
        this.add(x, 0, 0, y, 0, 0);
        if (cx || cy) this.add(1, 0, 0, 1, -cx, -cy);
        return this;
    }

    /**
     * Rotates the matrix by a given angle and position
     *
     * @param {number} a Angle of rotation
     * @param {number|Point} [xParam] Rotation point on the x-axis
     *                            or Point object which specifies the rotation point
     * @param {number} [yParam] Rotation point on the y-axis
     * @return {Matrix}
     * @chainable
     */
    rotate(a, xParam, yParam) {
        a = rad(a);
        const { x, y } = this._normalizeCoordinates(xParam, yParam);

        const cos = +Math.cos(a).toFixed(9);
        const sin = +Math.sin(a).toFixed(9);
        this.add(cos, sin, -sin, cos, x, y);
        this.add(1, 0, 0, 1, -x, -y);
        return this;
    }

    /**
     * Ensure correct coordinates
     *
     * @param {number} x
     * @param {number} y
     * @return {Object}
     * @private
     */
    _normalizeCoordinates(x, y) {
        if (x instanceof Point) return x;

        return { x: x || 0, y: y || 0 };
    }

    /**
     * Return x coordinate for given point after transformation described
     * by the matrix. See also Matrix#y
     *
     * @param  {number} x Origin on the x-axis
     * @param  {number} y Origin on the y-axis
     * @return {number} Transformed destination on the x axis
     */
    x(x, y) {
        return (x * this.a) + (y * this.c) + this.e;
    }

    /**
     * Return y coordinate for given point after transformation described
     * by the matrix. See also Matrix#x
     *
     * @param  {number} x Origin on the x-axis
     * @param  {number} y Origin on the y-axis
     * @return {number} Transformed destination on the y axis
     */
    y(x, y) {
        return (x * this.b) + (y * this.d) + this.f;
    }

    /**
     * Returns string value for the given matrix parameter index. In example
     * `matrix.get(2);` will return the value for parameter b as a string.
     *
     * @private
     * @param  {number} i Index of parameter
     * @return {number} Value for parameter
     */
    get(i) {
        return +this[String.fromCharCode(97 + i)].toFixed(4);
    }

    /**
     * Returns a string notation for the current matrix values. This string
     * can be used as `transform`attribute on several SVGElements.
     *
     * @return {string} String representation of the current matrix
     */
    toString() {
        return `matrix(${[this.get(0), this.get(1), this.get(2), this.get(3), this.get(4), this.get(5)].join()})`;
    }

    /**
     * Return offset for the current matrix
     *
     * @return {Array} Array with x and y offset
     * @return {number} return.0 Offset on x-axis
     * @return {number} return.1 Offset on y-axis
     */
    offset() {
        return [this.e.toFixed(4), this.f.toFixed(4)];
    }

    /**
     * Splits current matrix into primitive transformations.
     *
     * @return {Object} Object containing primitive transformations.
     * @return {number} return.dx Translation on x-axis
     * @return {number} return.dy Translation on y-axis
     * @return {number} return.scalex Scale on x-axis
     * @return {number} return.scaley Scale on y-axis
     * @return {number} return.shear Shear
     * @return {number} return.rotate Rotation in degrees
     * @return {boolean} return.isSimple Could it be represented via simple transformations
     */
    split() {
        const out = {};
        // translation
        out.dx = this.e;
        out.dy = this.f;

        // scale and shear
        const row = [[this.a, this.c], [this.b, this.d]];
        out.scalex = Math.sqrt(norm(row[0]));
        normalize(row[0]);

        out.shear = (row[0][0] * row[1][0]) + (row[0][1] * row[1][1]);
        row[1] = [row[1][0] - (row[0][0] * out.shear), row[1][1] - (row[0][1] * out.shear)];

        out.scaley = Math.sqrt(norm(row[1]));
        normalize(row[1]);
        out.shear /= out.scaley;

        // rotation
        const sin = -row[0][1];
        const cos = row[1][1]; // eslint-disable-line prefer-destructuring

        if (cos < 0) {
            out.rotate = deg(Math.acos(cos));
            if (sin < 0) {
                out.rotate = 360 - out.rotate;
            }
        } else {
            out.rotate = deg(Math.asin(sin));
        }

        out.isSimple = !+out.shear.toFixed(9) && (out.scalex.toFixed(9) === out.scaley.toFixed(9) || !out.rotate);
        out.isSuperSimple = !+out.shear.toFixed(9) && out.scalex.toFixed(9) === out.scaley.toFixed(9) && !out.rotate;
        out.noRotation = !+out.shear.toFixed(9) && !out.rotate;
        return out;
    }

    /**
     * Return transform string that represents given matrix
     *
     * @param {string} shorter
     * @return {string} Transform string
     */
    toTransformString(shorter) {
        const s = shorter || this.split();
        if (s.isSimple) {
            s.scalex = +s.scalex.toFixed(4);
            s.scaley = +s.scaley.toFixed(4);
            s.rotate = +s.rotate.toFixed(4);
            return (s.dx || s.dy ? `t${[s.dx, s.dy]}` : ' ')
                + (s.scalex !== 1 || s.scaley !== 1 ? `s${[s.scalex, s.scaley, 0, 0]}` : ' ')
                + (s.rotate ? `r${[s.rotate, 0, 0]}` : ' ');
        }
        return `m${[this.get(0), this.get(1), this.get(2), this.get(3), this.get(4), this.get(5)]}`;
    }
}
